import { useEffect, useRef, useState } from "react"

import { AnimationNotices } from "./components/AnimationNotices"
import { AnimationStrip } from "./components/AnimationStrip"
import { PromoPopupManager } from "../../../../services/promo_popup_manager"
import { trackAnalyticsEvent } from "../../../../services/track_analytics_event"
import { HotspotPicker, Notice } from "../../../shared/components"
import { stripExtension } from "../../../shared/services"
import { DownloadCard } from "../../components/DownloadCard"
import { HotspotControls } from "../../components/HotspotControls"
import { MoreOptions } from "../../components/MoreOptions"
import { SourceBar } from "../../components/SourceBar"
import { SourceNotices } from "../../components/SourceNotices"
import { TryZone } from "../../components/TryZone"
import { MAX_FRAMES } from "../../config"
import { useCursorData } from "../../contexts/CursorDataContext"
import { useCreateWindowsCursor } from "../../hooks/mutations/useCreateWindowsCursor"
import { formatLabel } from "../../services/formatLabel"
import { isSameUpload } from "../../services/isSameUpload"
import { playbackSequence } from "../../services/playbackSequence"
import { saveFile } from "../../services/saveFile"
import { SourceLoader } from "../../services/SourceLoader"
import { READ_ERROR, SourceError } from "../../services/SourceLoader.errors"

import type { CreateWindowsCursorParams } from "../../api/WindowsCursorsApi.types"
import type { Frame } from "../../types/Frame"
import type { Playback } from "../../types/Playback"
import type { PreviewBackground } from "../../types/PreviewBackground"
import type { Source } from "../../types/Source"

interface Props {
  source: Source
  previewBackground: PreviewBackground
  onPreviewBackgroundChange: (previewBackground: PreviewBackground) => void
  onNextFile: () => void
}

export const AnimatedWorkbench = ({
  source,
  previewBackground,
  onPreviewBackgroundChange,
  onNextFile,
}: Props) => {
  const pickerRef = useRef<HTMLDivElement>(null)
  const {
    outputFrames,
    outputResampling,
    autoHotspot,
    setHotspot,
    setFrames,
    addFrames,
    setIsConverting,
  } = useCursorData()
  const [delayMs, setDelayMs] = useState(source.delayMs)
  const [playback, setPlayback] = useState<Playback>("loop")
  // Null while playing. Held as the frame, not its position, so it outlives frame edits.
  const [pausedFrame, setPausedFrame] = useState<Frame | null>(null)
  const [step, setStep] = useState(0)
  const [droppedFrames, setDroppedFrames] = useState(source.droppedFrames)
  const [isAddingFrames, setIsAddingFrames] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)
  const createCursor = useCreateWindowsCursor()

  useEffect(() => {
    pickerRef.current?.focus({ preventScroll: true })
  }, [])

  const { frames, hotspot } = source
  const isPlaying = pausedFrame === null
  const sequence = playbackSequence(frames.length, playback)
  // A paused frame that has since been removed falls back to the playback position.
  const pausedIndex = pausedFrame ? frames.indexOf(pausedFrame) : -1
  const activeIndex = pausedIndex >= 0 ? pausedIndex : sequence[step % sequence.length]
  const loopMs = sequence.length * delayMs

  useEffect(() => {
    if (!isPlaying) return

    const timer = window.setInterval(() => setStep((current) => current + 1), delayMs)
    return () => window.clearInterval(timer)
  }, [isPlaying, delayMs])

  // Reverse is the frames uploaded backward; ping-pong is the server's `alternate`.
  const uploadFrames = playback === "reverse" ? [...outputFrames].reverse() : outputFrames
  const params: CreateWindowsCursorParams = {
    frames: uploadFrames.map((frame) => frame.pixels.data),
    hotspotX: hotspot.x,
    hotspotY: hotspot.y,
    delayMs,
    alternate: playback === "pingpong",
    resampling: outputResampling,
    filename: source.filename,
  }
  const isCurrent =
    createCursor.variables !== undefined && isSameUpload(createCursor.variables, params)
  const result = createCursor.isSuccess && isCurrent ? createCursor.data : null
  const error = createCursor.isError && isCurrent ? createCursor.error : null

  const handleDownload = () => {
    setIsConverting(true)
    createCursor.mutate(params, {
      onSuccess: (file) => {
        saveFile(file)
        trackAnalyticsEvent("cursor_converter_download", {
          source_format: formatLabel(source.type),
          output_format: "ANI",
        })
        PromoPopupManager.recordActivity()
      },
      onSettled: () => setIsConverting(false),
    })
  }

  const handlePlayToggle = () => {
    if (isPlaying) {
      setPausedFrame(frames[activeIndex])
      return
    }

    // Playback picks up from the frame on screen.
    setStep(sequence.indexOf(activeIndex))
    setPausedFrame(null)
  }

  const handleFramesAdd = async (files: File[]) => {
    setIsAddingFrames(true)
    try {
      const added = await SourceLoader.loadFrames(files, MAX_FRAMES - frames.length)
      addFrames(added)
      setDroppedFrames((current) => current + files.length - added.length)
      setAddError(null)
    } catch (reason) {
      setAddError(reason instanceof SourceError ? reason.message : READ_ERROR)
    } finally {
      setIsAddingFrames(false)
    }
  }

  return (
    <div className="card gap-4 bg-base-200! p-4">
      <SourceBar
        source={source}
        frames={frames}
        previewBackground={previewBackground}
        isDone={Boolean(result)}
        isConverting={createCursor.isPending}
        onPreviewBackgroundChange={onPreviewBackgroundChange}
        onNextFile={onNextFile}
      />

      <SourceNotices />
      {addError && (
        <Notice tone="error" onDismiss={() => setAddError(null)}>
          {addError}
        </Notice>
      )}
      <AnimationNotices droppedFrames={droppedFrames} loopMs={loopMs} />

      <div className="grid gap-4 md:grid-cols-[auto_minmax(0,1fr)]">
        {/* Locked while converting, so the saved file always matches the hotspot on screen. */}
        <div
          inert={createCursor.isPending}
          className={`flex flex-col gap-3 transition-opacity md:w-70 ${
            createCursor.isPending ? "opacity-60" : ""
          }`}
        >
          <div className="w-full max-w-70 self-center rounded-box bg-base-300 p-3">
            <HotspotPicker
              ref={pickerRef}
              label="Cursor preview. Click to set the hotspot, arrow keys to nudge it."
              imageUrl={outputFrames[activeIndex].url}
              hotspot={hotspot}
              previewBackground={previewBackground}
              onChange={setHotspot}
              onPlayToggle={handlePlayToggle}
            />
          </div>
          <p className="-mt-1 text-center text-xs font-semibold text-muted-content">
            {hotspot.x === autoHotspot.x && hotspot.y === autoHotspot.y
              ? "Auto hotspot — click to move it"
              : "Drag or use arrow keys to nudge"}
          </p>
          <HotspotControls hotspot={hotspot} autoHotspot={autoHotspot} onChange={setHotspot} />
          <MoreOptions />
        </div>

        <div className="flex min-w-0 flex-col gap-3">
          <TryZone
            frames={outputFrames}
            playback={playback}
            delayMs={delayMs}
            hotspot={hotspot}
            resampling={outputResampling}
            previewBackground={previewBackground}
          />
          <DownloadCard
            outputName={`${stripExtension(source.filename)}.ani`}
            frameCount={frames.length}
            isPending={createCursor.isPending}
            isAddingFrames={isAddingFrames}
            result={result}
            error={error}
            onDownload={handleDownload}
            onDownloadAgain={saveFile}
          />
        </div>
      </div>

      <AnimationStrip
        frames={outputFrames}
        activeIndex={activeIndex}
        isPlaying={isPlaying}
        isAddingFrames={isAddingFrames}
        isLocked={createCursor.isPending}
        delayMs={delayMs}
        loopMs={loopMs}
        playback={playback}
        onPlayToggle={handlePlayToggle}
        onFrameSelect={(index) => setPausedFrame(frames[index])}
        onFramesChange={setFrames}
        onFramesAdd={(files) => void handleFramesAdd(files)}
        onDelayChange={setDelayMs}
        onPlaybackChange={(next) => {
          setPlayback(next)
          setStep(0)
        }}
      />
    </div>
  )
}
